import logger from "../../utils/logger";
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState({});

  const readTokenFromStorage = () => {
    const rawToken = localStorage.getItem("token");
    if (!rawToken) return null;

    try {
      const parsedToken = JSON.parse(rawToken);
      if (typeof parsedToken === "string" && parsedToken.trim() !== "") {
        return parsedToken;
      }
    } catch {
      // token em formato legado (string sem JSON.stringify)
    }

    const normalized = rawToken.replace(/^"|"$/g, "").trim();
    return normalized || null;
  };

  const setAccessToken = (token) => {
    if (!token) return;
    localStorage.setItem("token", JSON.stringify(token));
    api.defaults.headers.Authorization = `Bearer ${token}`;
  };

  const reconnectSocketWithLatestToken = () => {
    if (!socket || typeof socket !== "object") return;

    if (typeof socket.forceReconnect === "function") {
      socket.forceReconnect();
      return;
    }

    if (typeof socket.connect === "function") {
      socket.connect();
    }
  };

  const disconnectSocket = (clearState = true) => {
    if (socket && typeof socket.disconnect === "function") {
      socket.disconnect();
    }
    if (clearState) {
      setSocket({});
    }
  };

  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const token = readTokenFromStorage();
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
          setIsAuth(true);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Tenta renovar token tanto para 401 quanto 403 (backend pode responder de formas diferentes)
        // Evita loop infinito e evita tentar refresh nas proprias rotas de auth.
        const status = error?.response?.status;
        const isAuthRoute =
          typeof originalRequest?.url === "string" &&
          (originalRequest.url.includes("/auth/login") ||
            originalRequest.url.includes("/auth/refresh_token") ||
            originalRequest.url.includes("/auth/logout"));

        if ((status === 401 || status === 403) && !originalRequest?._retry && !isAuthRoute) {
          originalRequest._retry = true;

          try {
            const { data } = await api.post("/auth/refresh_token");
            if (data?.token) {
              setAccessToken(data.token);
              reconnectSocketWithLatestToken();
              return api(originalRequest);
            }
          } catch {
            // cai para logout abaixo
          }
        }

        if (status === 401) {
          localStorage.removeItem("token");
          api.defaults.headers.Authorization = undefined;
          disconnectSocket();
          setUser({});
          setIsAuth(false);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  useEffect(() => {
    const token = readTokenFromStorage();
    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          if (data?.token) {
            setAccessToken(data.token);
          }
          setIsAuth(true);
          setUser(data.user);
        } catch (err) {
          toastError(err);
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.keys(user).length && user.id > 0 && user.companyId) {
      const io = socketConnection({ user });
      if (io !== socket) {
        setSocket(io);
      }

      const onCompanyUser = (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
        }
      };

      io.on(`company-${user.companyId}-user`, onCompanyUser);

      return () => {
        io.off(`company-${user.companyId}-user`, onCompanyUser);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }

      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }

      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      const diff = moment(dueDate).diff(moment(moment()).format());
      const before = moment(moment().format()).isBefore(dueDate);
      const dias = moment.duration(diff).asDays();

      if (before === true) {
        disconnectSocket(false);
        setAccessToken(data.token);
        localStorage.setItem("companyDueDate", vencimento);
        setUser(data.user);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        if (Math.round(dias) < 5) {
          toast.warn(`Sua assinatura vence em ${Math.round(dias)} ${Math.round(dias) === 1 ? "dia" : "dias"} `);
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.\nEntre em contato com o Suporte para mais informacoes! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      disconnectSocket();
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      logger.info(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;
